// Regel „wer muss unterschrieben haben, damit ein Protokoll abgeschlossen werden darf".
//
// Sie lag zunächst nur in der Oberfläche (SchrittUnterschriften.tsx) und band damit
// ausschließlich den Assistenten. Die Liste ruft `schliesseAb` aber über „Jetzt senden"
// ein zweites Mal auf — ein bei Schritt 2 abgebrochener Entwurf ließ sich darüber
// endgültig abschließen: PDF ohne Unterschriften, Mail an Mieter und Eigentümer, Entwurf
// danach gelöscht. Deshalb liegt die Regel jetzt als reine Funktion hier und wird an
// BEIDEN Stellen benutzt: als Sperrgrund/Knopfzustand in der Oberfläche und als harte
// Zusicherung in lib/uebergabe/abschluss.ts, die kein weiterer Aufrufer umgehen kann.
//
// Fachlich: Übergeber und Übernehmer sind die Vertragsparteien, ihre Unterschriften
// tragen das Dokument als Beweismittel. Der Zeuge (meist der erfassende Mitarbeiter)
// ist freiwillig — sein Fehlen darf einen Abschluss nie blockieren.
import type { Art, Partei, Protokoll, Unterschrift } from './typen';

export const ROLLEN_LABEL: Record<Partei['rolle'], string> = {
  uebergeber: 'Übergeber',
  uebernehmer: 'Übernehmer',
  zeuge: 'Zeuge',
};

export const PFLICHT_ROLLEN: Partei['rolle'][] = ['uebergeber', 'uebernehmer'];
export const ALLE_ROLLEN: Partei['rolle'][] = ['uebergeber', 'uebernehmer', 'zeuge'];

export interface AbschlussBereitschaft {
  bereit: boolean;
  /** Deutscher Klartext, warum noch nicht abgeschlossen werden kann; null wenn bereit. */
  grund: string | null;
}

/**
 * Ist diese Rolle bei dieser Art die VERMIETERSEITE — also die Seite, die der Assistent
 * planmäßig kontaktlos anlegt? verteileRollen() (lib/uebergabe/bezug.ts) legt sie als
 * leere Partei an, weil Vermieter/Verwalter in keiner Stammdatentabelle des Bezugs steht.
 * Sie steht bei der Übergabe selbst im Raum, unterschreibt dort und erwartet keine
 * Zustellung per Mail — nur ihre fehlende Mailadresse darf stillschweigend hingenommen
 * werden.
 *
 *   einzug -> Übergeber  (der Mieter zieht ein und ist der Übernehmer)
 *   auszug -> Übernehmer (der Mieter zieht aus und ist der Übergeber)
 *   kauf   -> KEINE. Verkäufer und Käufer kommen beide aus der Eigentümer-Tabelle; beide
 *             sind echte Beteiligte mit Zustellerwartung. Ein Käufer, der noch nicht in
 *             den Stammdaten steht und vor Ort nur mit Namen erfasst wird, MUSS gemeldet
 *             werden, wenn seine Mailadresse fehlt.
 *
 * Ein Zeuge (optional, keine Pflichtpartei mehr — s. PFLICHT_ROLLEN) wird ohnehin nie
 * gemeldet (istPflichtPartei, supabase/functions/uebergabe-finalisieren/empfaenger.ts) —
 * er ist bei keiner Art Vermieterseite.
 *
 * Dieselbe Zuordnung steht ein zweites Mal in
 * supabase/functions/uebergabe-finalisieren/empfaenger.ts (istVermieterseite): Die Edge
 * Function läuft unter Deno und kann lib/ nicht importieren, prüft das Merkmal aber
 * eigenständig gegen die Art gegen, statt dem Client zu glauben. Ändert sich die Zuordnung,
 * müssen beide Stellen mitziehen — beide sind gegen alle drei Arten getestet.
 */
export function istVermieterseite(art: Art, rolle: Partei['rolle']): boolean {
  if (art === 'einzug') return rolle === 'uebergeber';
  if (art === 'auszug') return rolle === 'uebernehmer';
  return false;
}

/**
 * Schreibt den bei der Unterschrift eingetragenen NAMEN in die zugehörige Partei zurück —
 * ausschließlich in ein leeres Namensfeld; eine in Schritt 1 erfasste Angabe wird nie
 * überschrieben.
 *
 * Review-Fund W1: Beim EINZUG legt verteileRollen() eine leere Übergeber-Partei an (der
 * Vermieter/Verwalter steht in keiner Stammdatentabelle des Bezugs). Unterschreiben
 * konnte er, sein Name landete aber nur in `unterschriften` — und das PDF filtert leere
 * Parteien über istBefuellt() aus dem Abschnitt „Beteiligte" heraus. Ergebnis bei JEDEM
 * Einzug: auf der Unterschriftsseite steht der Übergeber, im Abschnitt „Beteiligte" fehlt
 * er. Der Rückschreibe-Schritt hier schließt die Lücke an der Quelle, statt sie im PDF zu
 * kaschieren.
 *
 * Rollout-Fund 3: Der ORT wird bewusst NICHT mehr mit zurückgeschrieben. Er ist der Ort
 * der Unterschrift, keine Anschrift — und er wird beim Öffnen von Schritt 6 automatisch
 * aus der Stadt des gewählten Objekts vorbelegt (SchrittUnterschriften.tsx). Als
 * `anschrift` der Partei stand im Beweisdokument dadurch die Objektstadt unter dem
 * Übergeber, als wäre es seine Adresse. Eine Anschrift, die niemand angegeben hat, gehört
 * in ein Beweisdokument nicht.
 *
 * Rollout-Fund 4 (Melderegel): Diese Stelle WEISS, dass der Name nur aus der Unterschrift
 * stammt — also hält sie es auch fest (`nur_unterzeichner: true`), statt es die Edge
 * Function später an der Abwesenheit von Anschrift und Telefon raten zu lassen. Das Raten
 * konnte zwei Fälle nicht auseinanderhalten, die gegensätzlich behandelt werden müssen:
 * die planmäßig kontaktlose Vermieterseite (still) und eine echte Partei, die vor Ort von
 * Hand nur mit Namen erfasst wurde, weil Adresse und Mail noch fehlen (muss gemeldet
 * werden — sonst erfährt niemand, dass der neue Mieter sein Beweisdokument nie bekam).
 * Das Merkmal wird ausschließlich hier gesetzt; die Melderegel dazu steht in
 * supabase/functions/uebergabe-finalisieren/empfaenger.ts.
 *
 * Restkante zu Fund 4: „war vorher leer" war als alleiniges Kriterium zu weit gefasst —
 * leer ist nicht dasselbe wie Vermieterseite. Wer eine ECHTE Pflichtpartei in Schritt 1
 * leer lässt (ein neuer Mieter oder Käufer, der noch nicht in den Stammdaten steht) und
 * ihren Namen erst beim Unterschreiben einträgt, bekam damit ebenfalls das Merkmal — und
 * wurde nie gemeldet, obwohl er sein Beweisdokument mangels Mailadresse nie erhält. Genau
 * die Warnung, die im Kautionsstreit zählt, verschwand also durch die Hintertür ein
 * zweites Mal. Deshalb entscheidet jetzt die ROLLE in der jeweiligen ART
 * (istVermieterseite) darüber, ob das Merkmal überhaupt in Frage kommt; „war leer" bleibt
 * als zweite Bedingung daneben stehen, damit eine bereits als Kontakt geführte
 * Vermieterseite (Anschrift/Mail/Telefon erfasst, nur der Name fehlte) nicht
 * stillgestellt wird. Beim `kauf` entsteht das Merkmal nie.
 *
 * Gibt bei unveränderten Parteien BEWUSST dieselbe Referenz zurück (der Aufrufer steckt
 * das Ergebnis direkt in setEntwurf).
 */
export function uebernimmUnterschriftInParteien(
  parteien: Partei[],
  ziel: Pick<Partei, 'rolle' | 'pid'>,
  felder: { name: string },
  art: Art,
): Partei[] {
  const name = felder.name.trim();
  if (!name) return parteien;
  const { rolle } = ziel;

  // Nur die Vermieterseite dieser Art darf still bleiben. Für jede andere Rolle entsteht
  // das Merkmal nie — auch dann nicht, wenn ihre Zeile vor der Unterschrift leer war.
  const vermieterseite = istVermieterseite(art, rolle);

  // CRITICAL 1 (Abschluss-Review, Minor 6 im Ledger): über die Kennung statt über die
  // Rolle finden — bei mehreren Zeugen ist die Rolle allein nicht mehr eindeutig, ein
  // `.find(p => p.rolle === rolle)` träfe bei zwei Zeugen immer den ERSTEN, ein `.map()`
  // über dieselbe Bedingung würde bei leerem Namen sogar BEIDE mit demselben Namen
  // überschreiben. `gleichePartei` fällt nur auf die Rolle zurück, wenn `ziel` (oder die
  // Partei in der Liste) keine Kennung trägt — das trifft ausschließlich Altdaten, die nie
  // mehr als einen Zeugen kannten, dort bleibt der Rollen-Rückfall eindeutig.
  const vorhanden = parteien.find(p => gleichePartei(p, ziel));
  if (!vorhanden) {
    // Sollte für uebergeber/uebernehmer nicht vorkommen (verteileRollen legt sie immer
    // an), aber eine Unterschrift ohne jede Partei wäre genau der Fehler, den W1
    // beschreibt. Eine fehlende Zeile ist die denkbar leerste — es entscheidet also
    // allein die Rolle. Die Kennung des Ziels wird übernommen, sofern vorhanden, damit
    // die neu angelegte Partei mit derselben Unterschrift verknüpft bleibt.
    const merkmal = vermieterseite ? { nur_unterzeichner: true } : {};
    const pidFeld = ziel.pid ? { pid: ziel.pid } : {};
    return [...parteien, { rolle, name, anschrift: '', email: '', telefon: '', ...pidFeld, ...merkmal }];
  }
  if (vorhanden.name.trim()) return parteien;

  // Reiner Unterzeichner ist nur die Vermieterseite — und auch sie nur, solange zu ihr
  // NICHTS erfasst wurde. Steht dort schon eine Anschrift, Mail oder Telefonnummer (nur
  // der Name fehlte), ist sie als Kontakt geführt: kein Merkmal, fehlende Mail wird
  // gemeldet.
  const merkmal = vermieterseite && istLeerePartei(vorhanden) ? { nur_unterzeichner: true } : {};
  return parteien.map(p => (gleichePartei(p, ziel) ? { ...p, name, ...merkmal } : p));
}

/**
 * Felder, die Schritt 1 des Assistenten je Partei anbietet (SchrittRahmen.tsx).
 * `neue_anschrift` nur beim Übergeber und nur bei `art` 'auszug'/'kauf' sichtbar (s. dort) —
 * beeinflusst hier bewusst NICHT `nur_unterzeichner`: die Rolle, die dieses Feld zeigt
 * (Übergeber bei Auszug/Kauf), ist bei keiner der beiden Arten die Vermieterseite
 * (istVermieterseite), das Merkmal kann an ihr also ohnehin nie hängen.
 */
export type ParteiFeld = 'name' | 'anschrift' | 'email' | 'telefon' | 'neue_anschrift';

/**
 * Zeigt Schritt 1 (SchrittRahmen.tsx) das Feld „Neue Anschrift (nach Auszug)" für diese
 * Rolle bei dieser Art überhaupt an? Nur der Übergeber zieht bei `auszug` aus bzw.
 * verkauft bei `kauf` — bei `einzug` zieht niemand aus, dort bleibt das Feld ungenutzt.
 * Zentral hier (statt in SchrittRahmen.tsx dupliziert), weil dieselbe Bedingung auch
 * entferneUnpassendeMerkmale() unten braucht, um einen beim Wechsel der Art nicht mehr
 * passenden Wert abzuräumen.
 */
export function zeigtNeueAnschrift(art: Art, rolle: Partei['rolle']): boolean {
  return rolle === 'uebergeber' && (art === 'auszug' || art === 'kauf');
}

const leer = (wert: string | null | undefined): boolean => !(wert ?? '').trim();

/** Zu dieser Partei wurde noch NICHTS erfasst — der Platzhalter aus verteileRollen(). */
export function istLeerePartei(p: Partei): boolean {
  return leer(p.name) && leer(p.anschrift) && leer(p.email) && leer(p.telefon);
}

/** Mindestens ein Weg, auf dem diese Partei erreichbar wäre. */
export function hatKontaktweg(p: Partei): boolean {
  return !leer(p.anschrift) || !leer(p.email) || !leer(p.telefon);
}

/**
 * Ändert ein Partei-Feld in Schritt 1 — und entfernt dabei das Merkmal
 * `nur_unterzeichner`, sobald ein Kontaktweg (Anschrift, E-Mail oder Telefon) erfasst ist.
 *
 * Warum hier und nicht in der Oberfläche: Wer die Vermieterseite nachträglich mit Anschrift
 * oder Mailadresse ergänzt, hat sie damit zu einer geführten Kontaktpartei gemacht — ihre
 * fehlende Mailadresse muss ab dann wieder gemeldet werden. Bliebe das Merkmal stehen,
 * wäre sie für immer stillgestellt, und genau die Warnung, die im Kautionsstreit zählt,
 * käme nie.
 *
 * Eine bloße KORREKTUR des Namens hebt das Merkmal dagegen nicht auf: an der Schreibweise
 * zu feilen ändert nichts daran, dass die Partei keinen Kontaktweg hat und vor Ort
 * unterschrieben hat. Wird der Name dagegen ganz GELÖSCHT, ist von dem, was das
 * Rückschreiben angelegt hat, nichts mehr übrig — dann fällt das Merkmal mit. Sonst könnte
 * die Vorbelegung aus dem Vertrag (SchrittRahmen.tsx, greift nur bei leerem Namen) einen
 * echten Mieter in eine noch als „nur Unterzeichner" markierte Zeile schreiben und ihn
 * damit stillstellen.
 *
 * Gelöscht (statt auf false gesetzt) wird das Merkmal, weil „nicht vorhanden" in der
 * Melderegel dasselbe bedeutet wie „false" — das JSONB bleibt so frei von Ballast.
 *
 * CRITICAL 1 (Abschluss-Review): `ziel` ist die zu ändernde Partei selbst (nicht mehr nur
 * ihre Rolle) — bei zwei Zeugen identifiziert die Rolle allein keine Partei mehr eindeutig.
 * Ohne diese Umstellung schrieb Schritt 1 (SchrittRahmen.tsx) über `p.rolle !== rolle` in
 * JEDEN Zeugen gleichzeitig: Anschrift/E-Mail von Zeuge 2 landete auch bei Zeuge 1. Der
 * Abgleich läuft über `gleichePartei` (Kennung, mit Rückfall auf die Rolle nur, wenn keine
 * Kennung vorhanden ist) — identisch zu `gehoertZu`, nur für zwei Parteien statt Partei und
 * Unterschrift.
 */
export function setzeParteiFeld(
  parteien: Partei[],
  ziel: Pick<Partei, 'rolle' | 'pid'>,
  feld: ParteiFeld,
  wert: string,
): Partei[] {
  return parteien.map(p => {
    if (!gleichePartei(p, ziel)) return p;
    const aktualisiert: Partei = { ...p, [feld]: wert };
    const merkmalHinfaellig = hatKontaktweg(aktualisiert) || istLeerePartei(aktualisiert);
    if (aktualisiert.nur_unterzeichner && merkmalHinfaellig) delete aktualisiert.nur_unterzeichner;
    return aktualisiert;
  });
}

/**
 * Räumt nach einem WECHSEL DER ART auf: Das Merkmal `nur_unterzeichner` wird von jeder
 * Rolle entfernt, die unter der neuen Art keine Vermieterseite mehr ist. Ebenso räumt sie
 * `neue_anschrift` bei jeder Rolle ab, die das Feld unter der neuen Art nicht mehr zeigt
 * (Review-Fund: eine beim Übergeber unter „auszug"/"kauf" eingetragene neue Anschrift blieb
 * bisher als Datenleiche stehen, wenn danach auf „einzug" gewechselt wurde — das
 * Eingabefeld verschwindet zwar aus Schritt 1, der Wert reiste aber im JSONB weiter mit und
 * wurde im PDF unverändert als „Neue Anschrift (nach Auszug)" gedruckt, obwohl bei einem
 * Einzug niemand auszieht).
 *
 * Der Fall (`nur_unterzeichner`): In Schritt 6 unterschreibt beim Einzug die Vermieterseite
 * (Übergeber) — das Rückschreiben setzt dort das Merkmal. Wer danach in Schritt 1
 * zurückgeht und die Art auf „Auszug" stellt, verschiebt die Vermieterseite auf den
 * Übernehmer; das Merkmal hing bisher weiter am Übergeber, der jetzt der ausziehende
 * Mieter ist. Weder uebernimmUnterschriftInParteien (bricht bei bereits gefülltem Namen ab
 * und bewertet nichts neu) noch setzeParteiFeld (eine reine Namenskorrektur räumt bewusst
 * nicht auf) hätten es je wieder entfernt — die Warnung über eine echte Partei ohne
 * Mailadresse wäre dauerhaft ausgeblieben.
 *
 * Für die NEUE Vermieterseite wird das Merkmal bewusst NICHT nachträglich gesetzt: es
 * entsteht ausschließlich beim Rückschreiben einer Unterschrift in eine leere Partei
 * (uebernimmUnterschriftInParteien), und nur dort ist bekannt, dass der Name allein von
 * dort stammt. Hier nachzusetzen würde eine bereits erfasste Partei stillstellen.
 *
 * Diese Funktion ist die ANZEIGE-Seite der Sache. Der eigentliche Riegel für
 * `nur_unterzeichner` liegt serverseitig: ermittleUebersprungene
 * (supabase/functions/uebergabe-finalisieren/empfaenger.ts) spiegelt jedes Merkmal gegen
 * die Art des Protokolls und ignoriert es, wenn beides nicht zusammen passt — unabhängig
 * davon, was der Client geschrieben hat oder welche Altdaten existieren. `neue_anschrift`
 * hat serverseitig keinen entsprechenden Riegel (die Edge Function druckt sie bedingungslos,
 * wenn befüllt) — hier ist die einzige Stelle, die einen unpassenden Wert abräumt.
 *
 * Gibt bei unveränderten Parteien BEWUSST dieselbe Referenz zurück (wie die übrigen Helfer
 * hier — der Aufrufer steckt das Ergebnis direkt in setEntwurf).
 */
export function entferneUnpassendeMerkmale(parteien: Partei[], art: Art): Partei[] {
  const merkmalUnpassend = (p: Partei) => !!p.nur_unterzeichner && !istVermieterseite(art, p.rolle);
  const anschriftUnpassend = (p: Partei) => p.neue_anschrift !== undefined && !zeigtNeueAnschrift(art, p.rolle);
  const unpassend = (p: Partei) => merkmalUnpassend(p) || anschriftUnpassend(p);
  if (!parteien.some(unpassend)) return parteien;
  return parteien.map(p => {
    if (!unpassend(p)) return p;
    // Gelöscht statt auf false/leer gesetzt — „nicht vorhanden" bedeutet in der Melderegel
    // dasselbe wie „false", und das JSONB bleibt frei von Ballast (wie setzeParteiFeld).
    const bereinigt: Partei = { ...p };
    if (merkmalUnpassend(p)) delete bereinigt.nur_unterzeichner;
    if (anschriftUnpassend(p)) delete bereinigt.neue_anschrift;
    return bereinigt;
  });
}

/**
 * Ordnet zwei Partei-artige Objekte (Partei-Unterschrift oder Partei-Partei) einander zu:
 * über die Kennung, sobald BEIDE eine tragen, sonst über die Rolle. Der Rollen-Rückfall
 * setzt voraus, dass die Rolle das Gegenüber eindeutig identifiziert — das gilt nur, solange
 * es zu dieser Rolle höchstens eine Partei gibt (Übergeber/Übernehmer immer, ein einzelner
 * Zeuge in Altdaten). Gemeinsame Grundlage von `gehoertZu` (Unterschrift/Partei) und
 * `setzeParteiFeld`/`uebernimmUnterschriftInParteien` (Partei/Partei) — CRITICAL 1 des
 * Abschluss-Reviews: eine zweite, rollenbasierte Umgehung an einer dieser Stellen wäre
 * dieselbe Falle wieder gewesen.
 */
function gleichePartei(
  a: { rolle: Partei['rolle']; pid?: string },
  b: { rolle: Partei['rolle']; pid?: string },
): boolean {
  if (a.pid && b.pid) return a.pid === b.pid;
  return a.rolle === b.rolle;
}

/**
 * Gehört diese Unterschrift zu dieser Partei? Neu erfasste tragen beidseitig `pid`;
 * bei Altdaten (und abgeschlossenen, eingefrorenen Protokollen) entscheidet die Rolle
 * wie bisher. Ohne den Rückfall verlören alte Protokolle ihre Zuordnung.
 *
 * Der Rollen-Rückfall setzt voraus, dass die Rolle eine Partei eindeutig identifiziert —
 * das gilt nicht mehr, sobald mehrere Zeugen existieren. Genau deshalb versorgt
 * ergaenzeKennungen() unten beim Nachrüsten NICHT nur die Parteien, sondern über dieselbe
 * Zuordnung hier auch ihre Unterschriften mit — solange es zu diesem Zeitpunkt nur einen
 * Zeugen gibt (immer der Fall bei einem Entwurf von vor dieser Änderung), ist der
 * Rollen-Rückfall eindeutig und die nachgerüstete Kennung korrekt.
 */
export function gehoertZu(u: Unterschrift, p: Partei): boolean {
  return gleichePartei(u, p);
}

/** Neue Partei mit frischer Kennung — der einzige Ort, an dem eine `pid` entsteht. */
export function neuePartei(rolle: Partei['rolle'], felder: Partial<Partei> = {}): Partei {
  return {
    rolle, pid: crypto.randomUUID(),
    name: '', anschrift: '', email: '', telefon: '',
    ...felder,
  };
}

/** Einen weiteren, leeren Zeugen anhängen — Zeugen stehen immer am Ende der Liste. */
export function fuegeZeugenHinzu(parteien: Partei[]): Partei[] {
  return [...parteien, neuePartei('zeuge')];
}

/**
 * Rüstet fehlende Partei-Kennungen nach (Entwürfe von vor dieser Version kennen `pid`
 * nicht) — und versorgt dabei auch die vorhandenen UNTERSCHRIFTEN mit, nicht nur die
 * Parteien. Grund (Review-Fund, s. gehoertZu): Ein Entwurf von vor dieser Änderung hat
 * höchstens einen Zeugen, dessen Unterschrift ebenfalls keine `pid` trägt. Bekäme beim
 * Laden nur die Partei eine Kennung, bliebe die Unterschrift über den Rollen-Rückfall in
 * gehoertZu() zugeordnet — eindeutig, solange es bei genau einem Zeugen bleibt. Kommt
 * danach ein zweiter Zeuge hinzu, liefert derselbe Rollen-Rückfall für BEIDE Zeugen
 * `true`, und ein `.find()` träfe möglicherweise den falschen. Deshalb bekommt hier jede
 * Unterschrift ohne `pid` sofort die Kennung der Partei, zu der sie nach heutiger Regel
 * gehört — das ist zum Zeitpunkt des Nachrüstens noch eindeutig.
 *
 * Gibt bei nichts zu tun DIESELBEN Arrays zurück (beide, `parteien` und `unterschriften`),
 * damit der Aufrufer erkennt, dass er nicht speichern muss.
 *
 * Fix-Runde 1 (Review): der Schnellpfad prüfte ursprünglich NUR die Parteien. Das reichte
 * nicht — SchrittUnterschriften.tsx (Task 3, noch nicht umgestellt) schreibt weiterhin
 * Unterschriften OHNE `pid`, während jede Partei ab dieser Version sofort eine bekommt
 * (neuePartei). Für jeden ab jetzt neu unterschriebenen Entwurf galt also: Partei hat
 * eine Kennung, ihre Unterschrift nicht — der alte Schnellpfad (nur `parteien.every`)
 * hätte diesen Fall fälschlich als „nichts zu tun" durchgewunken, und genau die
 * Unterschrift, die den mehrdeutigen Rollen-Rückfall in gehoertZu() auslöst, hätte nie
 * eine Kennung bekommen. Der Schnellpfad prüft deshalb jetzt BEIDE Seiten: nichts zu tun
 * ist es nur, wenn zusätzlich keine Unterschrift ohne `pid` einer Partei zuordenbar ist
 * (gehoertZu fällt für Unterschriften ohne `pid` immer auf den Rollen-Rückfall zurück).
 */
export function ergaenzeKennungen(
  protokoll: Pick<Protokoll, 'parteien' | 'unterschriften'>,
): { parteien: Partei[]; unterschriften: Unterschrift[] } {
  const parteienVollstaendig = protokoll.parteien.every(p => p.pid);
  const unterschriftOffen = protokoll.unterschriften.some(
    u => !u.pid && protokoll.parteien.some(p => gehoertZu(u, p)),
  );
  if (parteienVollstaendig && !unterschriftOffen) {
    return { parteien: protokoll.parteien, unterschriften: protokoll.unterschriften };
  }
  const parteien = protokoll.parteien.map(p => (p.pid ? p : { ...p, pid: crypto.randomUUID() }));
  const unterschriften = protokoll.unterschriften.map(u => {
    if (u.pid) return u;
    const partei = parteien.find(p => gehoertZu(u, p));
    return partei ? { ...u, pid: partei.pid } : u;
  });
  return { parteien, unterschriften };
}

/**
 * Beschriftung einer Partei. Zeugen werden erst ab zwei durchnummeriert — bei genau
 * einem wäre „Zeuge 1" eine Zahl ohne Aussage.
 *
 * Review-Fund: Ohne `pid` fällt die Suche auf Referenzgleichheit (`x === p`) zurück. Der
 * Rest der Datei baut Parteien-Arrays aber durchgängig NEU auf (setzeParteiFeld,
 * entferneUnpassendeMerkmale — `.map()`/Spread) — das übergebene `p` ist dann nicht mehr
 * dasselbe Objekt wie sein Gegenstück in `parteien`, `findIndex` liefert `-1`, und ohne
 * Abfangen entstünde „Zeuge 0". Dasselbe gilt, wenn `p` gar nicht in der Liste steht.
 * `-1` ist daher kein Normalfall, sondern der Beweis, dass die Kennung fehlt — dann bleibt
 * es bei der schlichten Bezeichnung, statt eine falsche Nummer vorzutäuschen.
 */
export function parteiLabel(p: Partei, parteien: Partei[]): string {
  if (p.rolle !== 'zeuge') return ROLLEN_LABEL[p.rolle];
  const zeugen = parteien.filter(x => x.rolle === 'zeuge');
  if (zeugen.length <= 1) return ROLLEN_LABEL.zeuge;
  const index = zeugen.findIndex(x => (x.pid && p.pid ? x.pid === p.pid : x === p));
  if (index === -1) return ROLLEN_LABEL.zeuge;
  return `${ROLLEN_LABEL.zeuge} ${index + 1}`;
}

/**
 * Ersetzt den Blob einer Unterschrift in der Blob-Map des lokalen Entwurfs
 * (`LokalerEntwurf.unterschriften`, s. lib/uebergabe/lokal.ts) — und entfernt dabei den
 * ALTEN Pfad, sofern er vom neuen abweicht.
 *
 * MINOR 4 (Abschluss-Review): Der PNG-Pfad hängt seit den mehreren Zeugen an der
 * Partei-KENNUNG statt an der Rolle (SchrittUnterschriften.tsx: `${entwurf.id}/
 * unterschriften/${kartenSchluessel(partei)}.png`). Ein vor dieser Änderung begonnener
 * Entwurf trägt für eine bereits geleistete Unterschrift noch den alten, rollenbasierten
 * Pfad (z. B. „…/zeuge.png") — „Erneut unterschreiben" erzeugt dort einen NEUEN,
 * kennungsbasierten Pfad. Ohne dieses Aufräumen bliebe der alte Blob unreferenziert in der
 * Map liegen: `schliesseAb()` (lib/uebergabe/abschluss.ts) lädt ALLE Blobs dieser Map hoch,
 * die verwaiste PNG landete also im Beweis-Bucket, ohne dass das PDF sie referenziert.
 *
 * Überschreibt „Erneut unterschreiben" denselben Pfad (der Normalfall seit dieser Version,
 * `alterPfad === neuerPfad`), ändert sich am Ergebnis nichts — der Eintrag wird ohnehin nur
 * ersetzt.
 */
export function ersetzeUnterschriftBlob(
  blobs: Record<string, Blob>,
  neuerPfad: string,
  neuerBlob: Blob,
  alterPfad?: string,
): Record<string, Blob> {
  const naechste = { ...blobs, [neuerPfad]: neuerBlob };
  if (alterPfad && alterPfad !== neuerPfad) delete naechste[alterPfad];
  return naechste;
}

/** Prüft, ob Übergeber und Übernehmer unterschrieben haben (Zeuge freiwillig). */
export function pruefeAbschlussBereit(protokoll: Pick<Protokoll, 'unterschriften'>): AbschlussBereitschaft {
  const unterschriften = protokoll.unterschriften ?? [];
  const fehlend = PFLICHT_ROLLEN.filter(rolle => !unterschriften.some(u => u.rolle === rolle));
  if (fehlend.length === 0) return { bereit: true, grund: null };
  const namen = fehlend.map(rolle => ROLLEN_LABEL[rolle]).join(' und ');
  return { bereit: false, grund: `Zum Abschluss müssen noch unterschreiben: ${namen}.` };
}
